import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import _ from 'lodash';
import { ToastrService } from 'ngx-toastr';
import { ReplaySubject, filter, of, switchMap, takeUntil } from 'rxjs';

import { deleteRoomError, deleteRoomSuccess } from 'app/modules/social/state/home-page/selectors';
import { RoomSizeInfo } from 'app/modules/social/types/room';
import { Loggable } from 'app/utils/logging/loggable';
import Social from 'entity/Social';

import { RoomSizes } from '../pages/create-room/room-size-card/constants';
import { ScootaversePageService } from '../services/scootaverse-page.service';

@Component({
  selector: 'scootaverse-rooms',
  templateUrl: './scootaverse-rooms.component.html',
  styleUrls: ['./scootaverse-rooms.component.scss'],
  standalone: false,
})
export class ScootaverseRoomsComponent extends Loggable implements OnInit, OnDestroy {
  public rooms: Social[] = [];
  public loading = false;
  public showDeletedMessage = false;
  private destroyed$ = new ReplaySubject<boolean>(1);
  public scootaverseId: string | null = null;

  private roomSizes = RoomSizes;

  public constructor(
    private store: Store,
    private scootaversePageService: ScootaversePageService,
    private _toasterService: ToastrService,
    private route: ActivatedRoute,
  ) {
    super();
    this.setDisplayName('ScootaverseRoomsComponent');

    this.route.paramMap
      .pipe(
        switchMap((paramMap) => {
          const scootaverseId = paramMap.get('id');
          return of(scootaverseId); // Return an observable
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe((scootaverseId) => {
        this.scootaverseId = scootaverseId;
        this.fetchRooms();
      });

    this.store
      .select(deleteRoomSuccess)
      .pipe(
        filter((success) => success),
        takeUntil(this.destroyed$),
      )
      .subscribe(() => {
        this._toasterService.success('Room deleted successfully!', 'Success');
        this.fetchRooms();
      });

    this.store
      .select(deleteRoomError)
      .pipe(
        filter((error) => error),
        takeUntil(this.destroyed$),
      )
      .subscribe(() => {
        this._toasterService.error('Error! This room is open. The room must be closed to delete it.', 'Error');
      });

    this.scootaversePageService.selectedScootaverse$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((selectedScootaverse) => {
        if (selectedScootaverse) {
          this.rooms = selectedScootaverse.socials ?? [];
          this.loading = false;
        }
      });
  }

  public ngOnInit(): void {
    this.loading = true;
  }

  private fetchRooms(): void {
    if (!this.scootaverseId) {
      this.loading = false;
      void this.scootaversePageService.redirectToHome();
    } else {
      try {
        this.scootaversePageService.fetchRooms(this.scootaverseId);
      } catch (e) {
        this.error('Error fetching rooms: ', e);
        this._toasterService.error('Error trying to fetch rooms. Try again later.');
        this.loading = false;
      }
    }
  }

  public getRoomSizeByCapacity(room: Social): RoomSizeInfo {
    return {
      capacity: room.maxParticipants?.toString() ?? '',
      size: room.startingHeight?.toString() ?? '',
      label: this.roomSizes.find((sizeExample) => sizeExample.size === room.startingHeight)?.title,
    };
  }

  public setLoading($event: boolean) {
    this.loading = $event;
  }

  protected trackByRoomId(index: number, room: Social): string {
    return room.socialId ?? '';
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
    this.loading = false;
  }
}
